import React, { MutableRefObject, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { typography } from '../../theme';
import { InputError } from './InputError';
import { v4 as uuidv4 } from 'uuid';
import { colors, gray80 } from '../../theme/color';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  errorMessage?: string;
  label?: string;
  labelInstruction?: string;
  inputRef?: MutableRefObject<HTMLInputElement>;
  labelColor?: string;
  rows?: number;
  width?: 'full' | 'medium' | 'half' | 'quarter';
  noLabel?: boolean;
  testId?: string;
  id?: string;
};

// eslint-disable-next-line react/display-name
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      label,
      labelInstruction,
      errorMessage,
      children,
      className,
      inputRef,
      labelColor,
      id,
      width = 'medium',
      noLabel = false,
      testId,
      ...props
    },
    ref
  ) => {
    if (value === null) {
      value = '';
    }

    const inputId = useMemo(() => {
      if (id) {
        return id;
      }

      return uuidv4();
    }, [id]);

    const inputErrorId = `input-error-${inputId}`;

    return (
      <Container className={className} width={width} color={labelColor} errorMessage={errorMessage}>
        {!noLabel && (
          <Label
            color={labelColor}
            aria-hidden="true"
            isLabelInstructions={Boolean(labelInstruction)}
          >
            {label}
          </Label>
        )}
        {labelInstruction && (
          <LabelInstruction aria-hidden="true">{labelInstruction}</LabelInstruction>
        )}
        <InputContainer>
          <StyledInput
            as={props.rows ? 'textarea' : 'input'}
            aria-label={label}
            {...props}
            aria-describedby={inputErrorId}
            id={inputId}
            value={value}
            ref={ref || inputRef}
            data-testid={testId}
          />
          {children}
        </InputContainer>
        <InputError id={inputErrorId} error={errorMessage} />
      </Container>
    );
  }
);

export const InvertedInput = styled(Input)`
  label {
    color: ${({ color }) => color || colors.textWhite};
  }

  input {
    border-color: transparent;
  }

  input:hover {
    border-color: rgba(0, 0, 0, 0.3);
  }

  input:focus {
    border-color: rgba(0, 0, 0, 0.3);
  }

  input::placeholder {
    color: ${gray80};
    font-size: ${typography.fontSize.MEDIUM_MINUS};
  }
`;

const InputContainer = styled.div`
  display: flex;
  position: relative;
  margin: 0;
  padding: 0;
`;

export const Label = styled.label<{ color?: string; isLabelInstructions?: boolean }>`
  ${typography.inputLabelStyle}
  padding: ${({ isLabelInstructions }) => (isLabelInstructions ? '0 0 0 0' : '0 0 8px 0')};
  color: ${({ color }) => color || colors.black};
  min-height: 16px;
  :empty {
    display: none;
  }
`;

const LabelInstruction = styled(typography.TextSmall)`
  display: block;
  padding: 0 0 8px 0;
  color: ${colors.gray60};
  :empty {
    display: none;
  }
`;

const Container = styled.div<{
  width: 'full' | 'medium' | 'half' | 'quarter';
  errorMessage: string | undefined;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0;

  ${({ width }) =>
    width === 'medium'
      ? css`
          max-width: 30em;
        `
      : width === 'half'
        ? css`
            width: 50%;
          `
        : width === 'quarter'
          ? css`
              width: 25%;
            `
          : css`
              width: 100%;
            `};

  ${({ errorMessage }) =>
    errorMessage &&
    css`
      ${StyledInput} {
        border-color: ${colors.textError};
      }
    `}
`;

const StyledInput = styled.input`
  ${typography.textStyle}
  display: flex;
  font-size: 18px;
  padding: 16px;
  width: 100%;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
  background-color: ${colors.white};
  background-clip: padding-box;
  color: ${colors.black};

  transition:
    background-color 0.1s ease-in,
    border-color 0.1s ease-in;

  &:hover {
    border-color: rgba(0, 0, 0, 0.4);
  }

  &:focus {
    border-color: ${colors.brandPrimary};
    outline: none;
  }

  &::placeholder {
    ${typography.textStyle}
    color: ${colors.gray30};
    font-size: 18px;
  }
`;

type ICloudProps = {
  email: string;
  isLogin: boolean;
};

export const ICloudWarningMessage: React.FC<ICloudProps> = ({ email, isLogin }) => {
  if (!email) return null;
  if (!email.includes('@icloud.com')) return null;

  if (isLogin) {
    return (
      <ICloudWarningComponent>
        Emme saa tällä hetkellä lähetettyä sähköposteja iCloud-osoitteisiin. Kirjaudu käyttäen
        salasanaa tai kokeile toista kirjautumistapaa, kunnes saamme vian korjattua.
      </ICloudWarningComponent>
    );
  }

  return (
    <ICloudWarningComponent>
      Emme saa tällä hetkellä lähetettyä sähköposteja iCloud-osoitteisiin. Käytä toista
      sähköpostiosoitetta, kunnes saamme vian korjattua.
    </ICloudWarningComponent>
  );
};

const ICloudWarningComponent = styled.span`
  ${typography.textSmallStyle}
  line-height: 140%;
  display: inline-block;
  padding: 12px;
  margin: 24px 4px;
  background-color: ${colors.orderSummaryAlert};
`;
